import React from 'react'
import {
  Typography,
  Snackbar,
  TableCell,
  TableBody,
  TableRow,
  Table,
  Box,
  TableHead,
  TableContainer,
  Paper,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles';
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import { IPremixRequest, PremixRequest } from 'interfaces/IPremixRequest'
import { formatTimestamp } from 'shared/dateFormatter'

const useStyles = makeStyles((theme) => ({
  labelColumn: {
    maxWidth: 'min-content',
  },
  expandableDetails: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  hiddenDetails: {
    paddingTop: 0,
  },
  productRow: {
    cursor: 'pointer',
  },
}))

export default function PremixRequestList() {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [premixRequests, setPremixRequests] = React.useState<IPremixRequest[]>(
    []
  )
  const [isLoading, setIsLoading] = React.useState<any>(true)
  const [snackbarState, setSnackbarState] = React.useState({
    isSuccessSnackbarOpen: false,
    isFailureSnackbarOpen: false,
    alertMessage: '',
  })

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>({})

  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false)
  const [selectedRequest, setSelectedRequest] =
    React.useState<IPremixRequest | null>(null)

  React.useEffect(() => {
    dispatch({
      type: 'SET_PAGE_TITLE',
      payload: 'Daftar Request Ruahan In/Out',
    })
  }, [dispatch])

  React.useEffect(() => {
    loadPremixRequests()
  }, [])

  const getConfirmedQuantity = (
    premixRequest: PremixRequest,
    batchNumber: string
  ) => {
    const premixBatch = premixRequest.stockCard?.stockCardBatches.find(
      (premix) => premix.batchNumber === batchNumber
    )
    return premixBatch?.confirmedQuantity ?? null
  }

  const loadPremixRequests = async () => {
    setIsLoading(true)

    let url = '/premix/getPremixRequests'
    const param = {
      queryStringParameters: {
        includeDetails: 'TRUE',
      },
    }

    try {
      const response = await API.get('products', url, param)
      response.sort((a: IPremixRequest, b: IPremixRequest) => {
        const dateA = new Date(a.createdAt ?? 0)
        const dateB = new Date(b.createdAt ?? 0)
        return dateB.getTime() - dateA.getTime()
      })
      setPremixRequests(response)
    } catch (error) {
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal memuat order.`,
        isFailureSnackbarOpen: true,
      }))
      console.error(error)
    }

    setIsLoading(false)
  }

  const handleOpenCancelDialog = (req: IPremixRequest) => {
    setSelectedRequest(req)
    setCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false)
    setSelectedRequest(null)
  }

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return
    try {
      setIsLoading(true)
      await API.post('products', '/premix/cancelPremixRequest', {
        body: {
          premixRequestId: selectedRequest.premixRequestId,
        },
      })
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Berhasil membatalkan request.`,
        isSuccessSnackbarOpen: true,
      }))
      await loadPremixRequests()
    } catch (error) {
      console.error(error)
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal membatalkan ${selectedRequest.premixRequestDisplayId}`,
        isFailureSnackbarOpen: true,
      }))
    } finally {
      setIsLoading(false)
      handleCloseCancelDialog()
    }
  }

  const canCancel = (req: IPremixRequest) => {
    return req.stockCard && !req.stockCard.isConfirmed
  }

  const getCancelReason = (req: IPremixRequest) => {
    if (!req.stockCard) {
      return 'Tidak ada Kartu Stok terkait.'
    }
    if (req.stockCard.isConfirmed) {
      return 'Kartu Stok sudah dikonfirmasi.'
    }
    return null
  }

  return (
    <Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none"></TableCell>
                <TableCell>Nomor Request Ruahan</TableCell>
                <TableCell>Tanggal Request</TableCell>
                <TableCell>Nomor Dokumen Kartu Stok Masuk/Keluar</TableCell>
                <TableCell>In/Out</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Grid container justifyContent="center" alignItems="center">
                      <SmoothLoadingIndicator size={40} />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
              {premixRequests.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography>Tidak ada request ruahan </Typography>
                  </TableCell>
                </TableRow>
              )}
              {premixRequests.map((premixRequest) => (
                <React.Fragment key={premixRequest.premixRequestId}>
                  <TableRow
                    className={classes.productRow}
                    hover
                    onClick={(e) => {
                      setExpandedRows({
                        ...expandedRows,
                        [premixRequest.premixRequestId]:
                          !expandedRows[premixRequest.premixRequestId],
                      })
                    }}
                  >
                    <TableCell padding="none">
                      <IconButton aria-label="expand row" size="small">
                        {expandedRows[premixRequest.premixRequestId] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {premixRequest.premixRequestDisplayId || '-'}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp(
                        'id-ID',
                        premixRequest.createdAt
                      )}
                    </TableCell>
                    <TableCell>
                      {premixRequest.stockCard?.stockCardDisplayId ?? '-'}
                    </TableCell>
                    <TableCell>{premixRequest.inOutDirection}</TableCell>
                    <TableCell>
                      {canCancel(premixRequest) ? (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenCancelDialog(premixRequest)
                          }}
                        >
                          Batalkan
                        </Button>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {getCancelReason(premixRequest)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className={
                        expandedRows[premixRequest.premixRequestId]
                          ? classes.hiddenDetails
                          : classes.expandableDetails
                      }
                    >
                      <Collapse
                        in={expandedRows[premixRequest.premixRequestId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box>
                          <Grid container>
                            <Grid item xs={12}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">No. Bets</Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Qty Diminta (pcs)
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Qty Dikeluarkan (pcs)
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {premixRequest.batches.map((batch, index) => (
                                    <TableRow key={index}>
                                      <TableCell>{batch.batchNumber}</TableCell>
                                      <TableCell>
                                        {batch.qtyRequested}
                                      </TableCell>
                                      <TableCell>
                                        {getConfirmedQuantity(
                                          premixRequest,
                                          batch.batchNumber
                                        ) ?? 'Pending'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Grid>
                          </Grid>
                          <Box mt={2}>
                            <Typography>
                              <Box component="span" fontWeight="bold" display="inline">
                                Notes:{' '}
                              </Box>
                              {premixRequest.notes}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Konfirmasi Pembatalan Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Apakah anda yakin ingin membatalkan request berikut?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius="4px">
                <Typography variant="body2">
                  <strong>Nomor Request:</strong>{' '}
                  {selectedRequest.premixRequestDisplayId}
                </Typography>
                <Typography variant="body2">
                  <strong>Tanggal:</strong>{' '}
                  {formatTimestamp('id-ID', selectedRequest.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Kartu Stok:</strong>{' '}
                  {selectedRequest.stockCard?.stockCardDisplayId ?? '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Arah:</strong> {selectedRequest.inOutDirection}
                </Typography>

                {selectedRequest.batches && selectedRequest.batches.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Detail Ruahan:</strong>
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>No. Bets</TableCell>
                          <TableCell>Qty Diminta (pcs)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRequest.batches.map((batch, index) => (
                          <TableRow key={index}>
                            <TableCell>{batch.batchNumber}</TableCell>
                            <TableCell>{batch.qtyRequested}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
              {getCancelReason(selectedRequest) && (
                <Box mt={2}>
                  <MuiAlert severity="warning">
                    Tidak dapat dibatalkan: {getCancelReason(selectedRequest)}
                  </MuiAlert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>
            Batal
          </Button>
          {selectedRequest && canCancel(selectedRequest) && (
            <Button
              onClick={handleConfirmCancel}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? 'Memproses...' : 'Konfirmasi Batalkan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isSuccessSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={snackbarState.isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isFailureSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

